import NextImage from '@/components/image';
import NextLink from '@/components/next-link';
import { ProductListProvider } from '@/components/product-list-context';
import CurrencyIcon from '@/components/ui/icons/currency-icon';
import useItemListImpression from '@/hooks/useItemListImpression';
import useSelectItem from '@/hooks/useSelectItem';
import { event_search } from '@/utils/gtm';
import { validatePrice } from '@/utils/category';
import { PRODUCT_LISTS } from '@/utils/gtm/item-lists';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';

// One suggestion is its own component so it can hold the impression and
// selection hooks — they cannot be called from inside the list's `map`.
const SearchSuggestion = ({ item, index, isLast, onSelect, onSearch }) => {
  const t = useTranslations('product');
  const listRef = useItemListImpression(item, index);
  const selectItem = useSelectItem(item, index);

  const is_stock = item?.stock?.is_in_stock && item?.stock?.qty > 0;
  const requirePrescription =
    item?.prescription_upload_required &&
    item?.prescription_upload_required !== 'No';

  const prices = validatePrice(item?.prices_with_tax);

  const special = is_stock ? prices?.specialPrice : prices?.originalPrice;

  return (
    <li
      ref={listRef}
      className={` ${isLast ? 'mb-0' : 'border-b-border mb-2 border-b'} relative cursor-pointer rounded-[3px] p-2 hover:bg-gray-100`}
    >
      <NextLink
        href={`/${item?.innova_url}`}
        className='flex items-center gap-3'
        onClick={() => {
          onSearch();
          selectItem();
          onSelect();
        }}
      >
        <NextImage
          src={item?.image_url || item?._media_?.image?.[0]?.image}
          alt={item?.name}
          loading='lazy'
          width={60}
          height={60}
          className='size-14 rounded-lg object-contain p-1'
        />

        <div className='flex flex-1 flex-col flex-nowrap justify-between gap-4 md:flex-row'>
          <div>
            <p className='text-sm font-semibold text-gray-900 hover:underline'>
              {item?.name}
            </p>
            <div className='my-1.5 flex gap-2'>
              {requirePrescription && (
                <div
                  className={`flex h-4 w-fit items-center rounded-sm bg-[#FFFAD3] px-2 pb-0.5 text-center text-xs font-bold text-[#89701A]`}
                >
                  {t('prescriptionRequired')}
                </div>
              )}
              {is_stock && item?.offer_type ? (
                <div
                  className={`flex h-4 w-fit items-center rounded-sm bg-[#ffe8e8] px-2 text-xs text-[#c62828]`}
                >
                  {item?.offer_type || ''}
                </div>
              ) : null}
            </div>
            <div className='mt-1 flex gap-5'>
              <span
                className={`flex items-center gap-1 text-xs font-semibold text-gray-900 ${
                  prices?.originalPrice === special
                    ? 'hidden'
                    : prices?.specialPrice
                      ? 'line-through'
                      : ''
                }`}
              >
                {prices?.originalPrice} <CurrencyIcon className='size-3' />
              </span>
              {prices?.specialPrice && (
                <span className='flex items-center gap-1 text-xs font-bold text-[#B84B34]!'>
                  {special} <CurrencyIcon className='size-3' />
                </span>
              )}
            </div>
          </div>

          {!is_stock && (
            <div className='h-fit w-fit min-w-[85px] rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700'>
              {t('Out of stock')}
            </div>
          )}
        </div>
        {/* {item?.label?.map((lab, i) => (
                <span key={i}>{lab?.frontend_settings?.label_text}</span>
              ))} */}
      </NextLink>
    </li>
  );
};

const SearchPopup = forwardRef(({ data, handleQuerySelect, query }, ref) => {
  const items = data?.[0]?.items;

  return (
    // A new query is a new list view, so its suggestions report again.
    <ProductListProvider list={PRODUCT_LISTS.searchSuggestions} viewKey={query}>
      <ul
        ref={ref}
        className='border-border <NextImage hide-scrollbar absolute top-full left-0 z-50 mt-2 max-h-[500px] w-full min-w-72 overflow-x-hidden overflow-y-auto border bg-white p-2 shadow-xl sm:left-1/2 sm:-translate-x-1/2'
      >
        {items?.map((item, index) => (
          <SearchSuggestion
            key={item?.objectID}
            item={item}
            index={index}
            isLast={items?.length === index + 1}
            onSelect={handleQuerySelect}
            onSearch={() =>
              event_search({
                search_term: query,
                search_results_count: items?.length,
                search_type: 'suggestion',
              })
            }
          />
        ))}
      </ul>
    </ProductListProvider>
  );
});

SearchPopup.displayName = 'SearchPopup';

export default SearchPopup;
